import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  TextField,
  Typography,
} from "@mui/material";
import { useState, useEffect } from "react";
import { usePermiteAcesso } from "../../Components/Funcionais/Utils";
import { useNavigate } from "react-router-dom";
import GaugeChart from "react-gauge-chart";
import APIAxios from "../../Api/APIAxios";
import FormCamposEmpresa from "./FormCamposEmpresa";
import PageTitle from "../../Components/Layout/PageTitle";
import PageSubTitle from "../../Components/Layout/PageSubTitle";

const URL_CEP = "https://viacep.com.br/ws/18111100/json/";

const ConfiguracoesGerais = () => {
  usePermiteAcesso(1); // Empresas / Configurações

  const [avisoErro, setAvisoErro] = useState("");
  const [avisoSucesso, setAvisoSucesso] = useState("");
  const [loading, setLoading] = useState(false);
  const apiAxios = APIAxios();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    nome: "",
    status: "",
    cnpj: "",
    ie: "",
    im: "",
    cep: "",
    endereco: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    uf: "",
    telefone: "",
    email: "",
    espacoDisponivel: "",
    espacoUtilizado: "",
  });

  // ---- ESTADO PARA O GAUGE ----
  const [storageError, setStorageError] = useState(null);
  const [espacoUtilizado, setEspacoUtilizado] = useState(0);
  const [espacoDisponivel, setEspacoDisponivel] = useState(0);

  // Função para formatar bytes em KB, MB ou GB
  const formatarBytes = (bytes) => {
    if (bytes >= 1024 ** 4) return `${(bytes / 1024 ** 4).toFixed(1)} TB`;
    if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
    if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(2)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024 ** 2).toFixed(1)} KB`;
    return `${(bytes / 1024).toFixed(2)} KB`;
  };

  const formatarUsoEspaco = (usado, total) => {
    const percentual = ((usado / total) * 100).toFixed(2);
    return `Usado ${formatarBytes(usado)} de ${formatarBytes(total)}`;
  };


  const fetchInformacoes = async () => {
    try {
      setAvisoErro("");
      setLoading(true);

      const response = await apiAxios.get("/empresas");
      if (response?.data?.sucesso !== "SIM") {
        setAvisoErro(response?.data?.data);
      } else {
        const empresas = response.data.data || [];
        const empresa = empresas[0] || {};
        setFormData(empresa);

        // Pega os bytes e guarda no estado específico do armazenamento
        const usado = Number(empresa?.espacoUtilizado);
        const disponivel = Number(empresa?.espacoDisponivel);
        setEspacoUtilizado(isFinite(usado) ? usado : 0);
        setEspacoDisponivel(isFinite(disponivel) ? disponivel : 0);
        setStorageError(null);
      }
    } catch (error) {
      setAvisoErro(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInformacoes();
  }, []);

  const buscarCEP = async (cepInput) => {
    const cep = (cepInput || "").replace(/\D/g, "");
    if (cep.length !== 8) return;

    try {
      setLoading(true);
      const resp = await fetch(`https://viacep.com.br/ws/${cep}/json/`, {
        method: "GET",
      });

      if (!resp.ok) {
        setAvisoErro(`HTTP ${resp.status}`);
        return;
      }

      const data = await resp.json();
      if (data.erro) {
        setAvisoErro("CEP não encontrado");
        return;
      }

      setFormData((prev) => ({
        ...prev,
        cep: cep,
        endereco: data.logradouro || "",
        complemento: data.complemento || "",
        bairro: data.bairro || "",
        cidade: data.localidade || "",
        uf: data.uf || "",
      }));

      // Foco no campo número após preencher endereço
      setTimeout(() => {
        document.querySelector('input[name="numero"]')?.focus();
      }, 0);
    } catch (err) {
      setAvisoErro("CEP não localizado!");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event) => {
    setAvisoSucesso("");
    setAvisoErro("");

    const { name, value } = event.target;
    let valor = value?.toUpperCase?.() ?? "";

    if (name === "numero") {
      valor = value.replace(/\D/g, "");
    }

    if (name === "uf") {
      valor = valor.replace(/[^A-Z]/g, "").slice(0, 2);
    }

    if (name === "cep") {
      const novoCep = value.replace(/\D/g, "").slice(0, 8);

      setFormData((prevData) => {
        const cepAnterior = (prevData.cep || "").replace(/\D/g, "").slice(0, 8);
        const cepMudou = cepAnterior && novoCep !== cepAnterior;

        return {
          ...prevData,
          cep: novoCep,
          numero: cepMudou ? "" : prevData.numero, // limpa se CEP mudou
        };
      });
      return;
    }
    // Demais campos: fluxo padrão
    setFormData((prevData) => ({
      ...prevData,
      [name]: valor,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setAvisoErro("");
    setAvisoSucesso("");

    const estadosValidos = [
      "AC", "AL", "AP", "AM", "BA", "CE", , "SE",
      "DF", "ES", "GO", "MA", "MT", "MS", "TO",
      "MG", "PA", "PB", "PR", "PE", "PI", "SP",
      "RJ", "RN", "RS", "RO", "RR", "SC",
    ];

    const ufDigitada = formData.uf?.toUpperCase();

    if (!estadosValidos.includes(ufDigitada)) {
      setAvisoErro("UF inválida! Digite uma sigla de estado válida.");
      setLoading(false);
      return; // interrompe o envio
    }

    try {
      const response = await apiAxios.post("/empresa", formData);

      if (response.status === 200 || response.status === 201) {
        if (response.data.sucesso === "SIM") {
          setAvisoSucesso("Cadastro atualizado com sucesso!");
        } else {
          setAvisoErro(response?.data?.data);
        }
      } else {
        setAvisoErro(response?.data?.data);
      }
    } catch (error) {
      if (error?.response?.data?.data) {
        setAvisoErro(error?.response?.data?.data);
      } else {
        setAvisoErro("Erro na comunicação com o servidor.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVoltar = () => {
    navigate("/home");
  };

  return (
    <Box>
      <PageTitle
        caption="Configurações Gerais"
        setAvisoErro={setAvisoErro}
        avisoErro={avisoErro}
        avisoSucesso={avisoSucesso}
        setAvisoSucesso={setAvisoSucesso}
        loading={loading}
        accentStyle
      />

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: "flex",
          flexDirection: "column",
          margin: "auto",
          padding: 2,
          gap: 2,
          backgroundColor: "transparent",
          borderRadius: 1,
        }}
      >
        <FormCamposEmpresa
          formData={formData}
          handleChange={handleChange}
          buscarCEP={buscarCEP}
        />
        <Box
          sx={{
            position: "relative",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button
            variant="contained"
            type="submit"
            onClick={handleVoltar}
            sx={{ backgroundColor: "#1a3865" }}
          >
            Voltar
          </Button>
          <Button
            variant="contained"
            type="submit"
            disabled={loading}
            sx={{ backgroundColor: "#1a3865" }}
          >
            {loading ? "Registrando..." : "Atualizar"}
          </Button>

          {loading && (
            <CircularProgress
              size={24}
              sx={{
                color: "#647DEE",
                position: "absolute",
                top: "50%",
                left: "50%",
                marginTop: "-12px",
                marginLeft: "-12px",
              }}
            />
          )}
        </Box>
      </Box>

      <PageSubTitle caption="Informações de Armazenamento" />

      <Grid container>
        <Grid
          item
          xs={12}
          md={4}
          container
          paddingInline={2}
          justifyContent="center"
          sx={{ mb: 2 }}
        >
          <Card sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            boxShadow: "0 8px 28px rgba(26, 56, 101, 0.12), 0 2px 6px rgba(100,125,238,0.08)",
            borderRadius: "16px",
            border: "1px solid rgba(100, 125, 238, 0.15)",
            overflow: "hidden",
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0, left: 0, right: 0,
              height: "4px",
              background: "linear-gradient(90deg, #269FFB, #647DEE, #1A3865)",
            },
          }}>
            <CardContent sx={{ flexGrow: 1, pt: 3 }} align="center">
              <Typography sx={{
                fontWeight: 700,
                fontSize: "1rem",
                color: "#1A3865",
                letterSpacing: "0.4px",
                mb: 1,
              }}>
                Armazenamento
              </Typography>
              <GaugeChart
                align="center"
                id="productivity-gauge2"
                nrOfLevels={3}
                percent={(formData.espacoUtilizado / formData.espacoDisponivel).toFixed(3)}
                arcsLength={[0.8, 0.1, 0.1]}
                colors={["#81C784", "#FFD54F", "#E57373"]}
                arcWidth={0.2}
                arcPadding={0.01}
                textColor={"#1A3865"}
                needleColor={"#78909C"}
                animate={false}
                style={{ width: "15rem" }}
              />
              <Typography sx={{ color: "#6b7a99", fontSize: "0.85rem", mt: 1 }}>
                {formatarUsoEspaco(espacoUtilizado, espacoDisponivel)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid
          item
          xs={12}
          md={8}
          container
          paddingInline={2}
          justifyContent="center"
          sx={{ mb: 2 }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <TextField
                label="Registro Mais Antigo de Atividade"
                name="primeiroRegistro"
                type="text"
                value={formData.primeiroRegistro ?? ""}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={7}>
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField
                label="Registro Mais Antigo de Imagem"
                name="primeiraImagem"
                type="text"
                value={formData.primeiraImagem ?? ""}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            <Grid item xs={12} md={7}>
            </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );

  ConfiguracoesGerais.propTypes = {
    // Aceite número ou string (ex.: "87" ou 87 ou 0.87)
    overallProductivity: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
  };
};

export default ConfiguracoesGerais;
